import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { MotivoInList } from "./schema/motives.schema";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import { MHeaderElements } from "./schema/header-elements";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { MotivesActions } from "@/components/common/section-components/action/MotivesActions";
import { fetchMotivesHandler, handleDeleteMotivesHandler } from "./schema/handler-functions";

export const MotivesPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingMotivos, setLoadingMotivos] = useState<boolean>(false);
  const [motivos, setMotivos] = useState<MotivoInList[] | null | undefined>([]);
  const [selection, setSelection] = useState<string[]>([]);

  const [loadingDeleteMotives, setLoadingDeleteMotives] =
    useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchMotives = async (showLoad = true) => {
    try {
      await fetchMotivesHandler({
        setLoadingMotivos,
        setMotivos,
        navigate,
        fetchPermissions,
        showLoad,
      });
    } catch {
      // error ya manejado por el handler (toaster + navigate)
    }
  };

  useEffect(() => {
    if (isActive) fetchMotives();
  }, [isActive]);

  const { hasWriteAccess, fetchPermissions } = useRoles();

  const hasSelection = selection.length > 0;
  const indeterminate = Boolean(
    hasSelection && motivos && selection.length < motivos?.length,
  );

  const navigate = useNavigate();

  const handleEdit = (item: MotivoInList) => {
    navigate(AppRoutes.MOTIVE_EDIT.replace(":id", String(item?.id)));
  };

  const handleAdd = () => {
    navigate(AppRoutes.MOTIVE_ADD);
  };

  const getActionElements = (item: MotivoInList) => (
    <MotivesActions handleEdit={handleEdit} item={item} />
  );

  const { setOpen: setOpenConfirm, open: openConfirm } = useDisclosure();


  const handleDeletePolicies = async () => {
    handleDeleteMotivesHandler({
      fetchMotives,
      selection,
      setLoadingDeleteMotives,
      setOpenConfirm,
      setSelection,
      navigate,
      fetchPermissions,
    });
  };


  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="motives-page"
    >
      <SectionHeader
        title="Motivos"
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        disableDelete={!selection?.length}
        handleAdd={handleAdd}
        handleDelete={() => setOpenConfirm(true)}
        showButtons={hasWriteAccess(PermissionCode.MOTIVES)}
        loading={loadingMotivos}
      />

      {Boolean(!loadingMotivos && !motivos?.length) && <NoData />}
      {Boolean(!loadingMotivos && motivos?.length) && (
        <StandardTable
          indeterminate={indeterminate}
          items={
            motivos?.map((pol: MotivoInList) => ({
              ...pol,
            })) ?? []
          }
          selection={selection}
          setSelection={setSelection}
          headers={MHeaderElements}
          noActions={!hasWriteAccess(PermissionCode.MOTIVES)}
          noSelection={!hasWriteAccess(PermissionCode.MOTIVES)}
          actionElements={getActionElements}
          actionsHeaderLabel="Edición"
          multipleSelection
        />
      )}
      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirm}
          handleAccept={handleDeletePolicies}
          loadingAccept={loadingDeleteMotives}
          title="Eliminar motivos"
          subtitle="¿Está seguro de que desea realizar esta acción?"
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
